// src/components/resources/benchmarks/BenchmarkComparison.tsx

'use client'

import Link from 'next/link'
import { cn, formatNumber, formatCurrency } from '~/lib/utils'
import {
  compareToBenchmark,
  type BenchmarkSet,
  type BenchmarkSegment,
  type BenchmarkVerdict,
} from '~/lib/resources/benchmarks'

/** Tailwind accent per verdict (goodness space: green good → red poor). */
const VERDICT_STYLES: Record<BenchmarkVerdict, { badge: string; dot: string }> = {
  'well-above': { badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', dot: 'bg-emerald-500' },
  above: { badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', dot: 'bg-emerald-500' },
  'on-par': { badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', dot: 'bg-amber-500' },
  below: { badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300', dot: 'bg-rose-500' },
  'well-below': { badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300', dot: 'bg-rose-500' },
}

function formatValue(value: number, unit: BenchmarkSet['unit']): string {
  // Reuse the repo formatters (thousands separators, locale-aware) rather than
  // hand-rolled toFixed. Callers must pass a finite value (the component
  // returns null for non-finite), since these throw on NaN/±Infinity.
  switch (unit) {
    case 'ratio':
      return `${formatNumber(value, 2)}x`
    case 'currency':
      return formatCurrency(value, 2)
    case 'percent':
    default: {
      // Truncate (don't round) to 2 decimals so a value just under a benchmark
      // boundary never displays AS the threshold it failed — e.g. a 24.96% hook
      // rate must not read "25.0%" while the verdict says "below typical range".
      // Normalize FP noise first so exact inputs don't drop one ULP (e.g.
      // 29/100*100 = 28.999…996). Trailing zeros are dropped (22 → "22%").
      const normalized = Math.round(value * 1e6) / 1e6
      const truncated = Math.floor(normalized * 100) / 100
      return `${truncated}%`
    }
  }
}

/** Internal path for the canonical reference (data stores absolute URLs). */
function toPath(url?: string): string | undefined {
  if (!url) return undefined
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

interface BenchmarkComparisonProps {
  set: BenchmarkSet
  segment: BenchmarkSegment
  value: number
  className?: string
}

/**
 * "Your value vs. industry benchmark" card for calculator/tool result panels.
 * Reads the unit-tested comparison engine (`~/lib/resources/benchmarks`) so
 * every benchmark surface agrees. Instead of a flat percentile fill, it plots
 * your value as a marker on a track that highlights the cited typical range
 * (low→high) and its median — so you can see *where you sit relative to the
 * range*, not just a number. Links to the canonical benchmark page for the
 * full methodology and cited sources.
 */
export function BenchmarkComparison({ set, segment, value, className }: BenchmarkComparisonProps): JSX.Element | null {
  // A non-finite value (e.g. a divide-by-zero ROAS from adSpend=0) must not
  // render a benchmark verdict — it would be meaningless/misleading. Bail out.
  if (!Number.isFinite(value)) return null

  const result = compareToBenchmark(set, segment, value)
  const style = VERDICT_STYLES[result.verdict]

  // Map values onto a track that extends one half-span past each anchor, so the
  // typical range sits in the middle with headroom on both sides (matches the
  // engine's interpolation anchors: low→25th, median→50th, high→75th).
  const lowSpan = Math.max(segment.median - segment.low, 1e-9)
  const highSpan = Math.max(segment.high - segment.median, 1e-9)
  const floor = segment.low - lowSpan
  const ceil = segment.high + highSpan
  const pos = (v: number): number => Math.min(100, Math.max(0, ((v - floor) / (ceil - floor)) * 100))
  const bandLeft = pos(segment.low)
  const bandRight = pos(segment.high)
  const medianPos = pos(segment.median)
  const valuePos = pos(value)

  const methodologyPath = toPath(set.referenceUrl ?? set.glossaryUrl)
  const trackLabel = `Your ${set.metricLabel} of ${formatValue(value, set.unit)} is ${result.verdictLabel.toLowerCase()}. Typical range ${formatValue(segment.low, set.unit)} to ${formatValue(segment.high, set.unit)}, median ${formatValue(segment.median, set.unit)}.`

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Your {set.metricLabel} vs. <span className="font-semibold">{segment.segment}</span> benchmark
        </p>
        <div className="flex items-center gap-1.5">
          {segment.directional && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              Directional
            </span>
          )}
          <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold', style.badge)}>
            {result.verdictLabel}
          </span>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        Typical {formatValue(segment.low, set.unit)}–{formatValue(segment.high, set.unit)} · median{' '}
        {formatValue(segment.median, set.unit)}
      </p>

      {/* Range track: highlighted typical band + median tick + your-value marker. */}
      <div className="mt-2" role="img" aria-label={trackLabel}>
        <div className="relative h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            className="absolute inset-y-0 rounded-full bg-indigo-200 dark:bg-indigo-900/50"
            style={{ left: `${bandLeft}%`, width: `${Math.max(bandRight - bandLeft, 0)}%` }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-y-[-2px] w-px bg-gray-400 dark:bg-gray-500"
            style={{ left: `${medianPos}%` }}
            aria-hidden="true"
          />
          <div
            className={cn(
              'absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full shadow ring-2 ring-white dark:ring-gray-900',
              style.dot,
            )}
            style={{ left: `${valuePos}%` }}
            aria-hidden="true"
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
          <span>typical range (low – median – high)</span>
          <span className="font-medium text-gray-600 dark:text-gray-300">
            You: {formatValue(value, set.unit)}
          </span>
        </div>
      </div>

      {segment.note && <p className="mt-2.5 text-xs text-gray-500 dark:text-gray-400">{segment.note}</p>}

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500">
        <span>Sources: {segment.sources.join('; ')}.</span>
        {methodologyPath && (
          <Link
            href={methodologyPath}
            className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Full benchmarks &amp; methodology →
          </Link>
        )}
      </div>
    </div>
  )
}
